import Idea from "@/types/Idea";
import IdeaProperty from "@/types/IdeaProperty";
import Plain from "@/types/Plain";
import {isNullOrUndefined} from "@/utils/utils"
import {t} from "@/i18n"

export function emptyIdea(): Idea {
    return {alias: '', name: '', pluralName: '', properties: []}
}
/**
 * A propriedade "nome" que toda coleção ganha quando se escolhe ser identificada por
 * nome. Estava copiada no ClassDialog e no CreateClass.
 */
export function nameProperty(): IdeaProperty {
    return {
        alias: t('name-alias'),
        name: t('name'),
        type: 'text',
        required: true,
        unique: true,
        identifier: true
    }
}

/**
 * A propriedade só com o que o designer escreve, sem o que o servidor deriva dela (`dependsOn`).
 *
 * É o ponto de partida de quem EDITA uma propriedade. O formulário devolve só o que é escrito, e
 * o `updatedProps` compara a união das chaves: com o derivado na origem, toda edição de calculada
 * pareceria ter mudanças e mandaria `dependsOn: null` no PATCH.
 */
export function designedProperty(property: IdeaProperty): IdeaProperty {
    const designed = {...property}
    delete designed.dependsOn
    delete designed.optionFilterDependsOn
    //O de cada regra também: o servidor recusa campo a mais dentro da regra
    if (designed.rules) designed.rules = designed.rules.map(rule => {
        const written = {...rule}
        delete written.dependsOn
        return written
    })
    return designed
}

/**
 * As chaves que mudaram, com o valor novo — `null` no que saiu, que o servidor traduz em `$unset`.
 *
 * Por VALOR, e não por referência: o formulário remonta listas e objetos (as regras, o `itemCount`)
 * a cada edição, e pela referência eles pareceriam sempre mudados — o diálogo pediria para
 * confirmar o fechamento sem mudança nenhuma, e o PATCH os mandaria de novo à toa.
 */
export function updatedProps<T extends Plain>(originalObject: T, updatedObject: T) {
    let keys = Object.keys(originalObject)
    keys.push(...Object.keys(updatedObject))
    keys = [... new Set(keys)]
    const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b)
    const entries = keys.filter(k => !same(originalObject[k], updatedObject[k])).map(k => [k, isNullOrUndefined(updatedObject[k])? null : updatedObject[k]])
    return Object.fromEntries(entries) as Partial<T> | Record<keyof T, null>
}


type AnyObject = Record<string, any>;

export function deepStructuredDiff(obj1: AnyObject, obj2: AnyObject): AnyObject {
    const result: AnyObject = {};
    const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of keys) {
        const has1 = Object.prototype.hasOwnProperty.call(obj1, key);
        const has2 = Object.prototype.hasOwnProperty.call(obj2, key);

        const val1 = obj1[key];
        const val2 = obj2[key];

        // Ignorar funções em ambos
        if (typeof val1 === "function" || typeof val2 === "function") {
            continue;
        }

        if (has1 && !has2) {
            // Campo removido
            result[key] = null;
        } else if (!has1 && has2) {
            // Campo adicionado
            result[key] = val2;
        } else if (isObject(val1) && isObject(val2)) {
            const nested = deepStructuredDiff(val1, val2);
            if (Object.keys(nested).length > 0) {
                result[key] = nested;
            }
        } else if (Array.isArray(val1) && Array.isArray(val2)) {
            if (JSON.stringify(val1) !== JSON.stringify(val2)) {
                result[key] = val2;
            }
        } else if (val1 !== val2) {
            result[key] = val2;
        }
    }

    return result;
}

/**
 * O que um diálogo de edição sabe do rascunho, tirado de uma comparação só — como a edição do item faz
 * com o `instanceDiff` (pedido do usuário):
 * - `changes`: o que mudou, e só isso vai no PATCH; vazio ao criar, que manda o rascunho inteiro;
 * - `unchanged`: editando sem mudança nenhuma — o salvar desabilita;
 * - `dirty`: há o que perder ao sair — o aviso da edição pendente. Ao criar, comparado à semente (o que
 *   o diálogo abriu preenchido), e não ao vazio: abrir e fechar não pergunta nada.
 *
 * Os diálogos e as páginas de edição de coleção, fluxo e domínio tinham isso escrito igual, cada um; o
 * de propriedade, o seu. A página só edita, e não tem semente. `diff`: o profundo por padrão; a
 * propriedade usa o `updatedProps`, que manda a chave de cima inteira — o servidor troca a lista de
 * regras e o `itemCount` inteiros.
 */
export type DraftState = { changes: AnyObject, unchanged: boolean, dirty: boolean }

export function draftState(original: AnyObject | undefined, draft: AnyObject, seed: AnyObject = {},
                           diff: (before: AnyObject, after: AnyObject) => AnyObject = deepStructuredDiff): DraftState {
    const changes = original ? diff(original, draft) : {}
    return {
        changes,
        unchanged: !!original && Object.keys(changes).length === 0,
        dirty: Object.keys(original ? changes : diff(seed, draft)).length > 0
    }
}

function isObject(val: any): val is AnyObject {
    return typeof val === "object" && val !== null && !Array.isArray(val);
}
